import React, { SyntheticEvent, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import { CheckCircleOutline, DeleteForever, InfoOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';

import { Suppliers } from '@luxuryescapes/contract-svc-bedbank/constants/suppliers';

import MappingConfidence from '~/components/Bedbank/Properties/Edit/RoomMapping/MappingConfidence';
import RoomSourceCommentModal from '~/components/Bedbank/Properties/Edit/RoomMapping/RoomSourceCommentModal';

import { chipColorByStatus } from '../common';

import RoomSourceContentModal from './RoomSourceContentModal';
import ToBeMappedRoomElement from './ToBeMappedRoomElement';
import { groupMappedRoomsBySupplierName } from './grouping';
import { buildAutomappingTargets, computeAutomappingState, computeVerificationState } from './roomStateUtils';
import type { AutomappingFlagToggleTarget, MappedRoom, MappingChange, PropertyRoom, SourceRoom } from './types';
import { isMappedRoomGroup } from './types';
import { generateOccupancyText, generateRoomSizeText, generateViewsText } from './util';

/**
 * Highlights matching text in a string by wrapping it in a styled span
 */
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || !searchTerm.trim()) {
    return text;
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const lowerText = text.toLowerCase();
  const startIndex = lowerText.indexOf(normalizedSearch);

  if (startIndex === -1) {
    return text;
  }

  const endIndex = startIndex + normalizedSearch.length;
  return (
    <>
      {text.substring(0, startIndex)}
      <Box
        component="span"
        sx={{
          backgroundColor: 'warning.light',
          color: 'warning.contrastText',
          fontWeight: 'bold',
          px: 0.5,
          borderRadius: 0.5,
        }}
      >
        {text.substring(startIndex, endIndex)}
      </Box>
      {text.substring(endIndex)}
    </>
  );
};

interface Props {
  room: PropertyRoom;
  property: App.Bedbank.PropertyEdit;
  onSelect: (room: PropertyRoom, supplier: App.Bedbank.Suppliers) => void;
  isSelected: boolean;
  onRemoveMappedRoom: (room: MappedRoom | Array<MappedRoom>) => void;
  verifyMappedRoomModel: (room: MappedRoom) => void;
  newMappings: Array<MappingChange>;
  appliedButNotSavedRooms: Array<SourceRoom>;
  clearNewMappings: () => void;
  removeSingleMapping: (supplierRoomId: string, supplier: string) => void;
  removePayload: App.Bedbank.SupplierRoomMappingPayload;
  automappingFlagPayload: App.Bedbank.SupplierRoomAutomappingFlagPayload;
  handleSetAutomappingFlags: (targets: AutomappingFlagToggleTarget | Array<AutomappingFlagToggleTarget>) => void;
  enableMLMatching: boolean;
  searchTerm?: string;
  matchedMappingIds?: Array<string>;
  mappedSupplierFilter?: string;
  mlMatchingCandidates?: Record<string, import('~/services/BedbankService').SourceRoomMLResult>;
  extraInclusionsByKey?: Map<string, Array<{ code: string; label: string }>>;
}

const ControlRoomElement = React.memo(
  React.forwardRef<HTMLDivElement, Props>(function ControlRoomElement(
    {
      room,
      property,
      onSelect,
      isSelected,
      onRemoveMappedRoom,
      verifyMappedRoomModel,
      newMappings,
      appliedButNotSavedRooms,
      clearNewMappings,
      removeSingleMapping,
      removePayload,
      automappingFlagPayload,
      handleSetAutomappingFlags,
      enableMLMatching,
      searchTerm,
      matchedMappingIds,
      mappedSupplierFilter,
      mlMatchingCandidates,
      extraInclusionsByKey,
    },
    ref,
  ) {
    const [confirmDelOpen, setConfirmDelOpen] = useState<Array<MappedRoom> | null>(null);
    const currentMappings = useMemo(() => {
      const filteredRooms = room.mappedRooms.filter(
        (sp) =>
          sp.externalId !== room.externalId &&
          !removePayload?.some((tr) => sp.externalId === tr.supplierRoomId && sp.supplier === tr.supplier),
      );

      const supplierFilteredRooms = mappedSupplierFilter
        ? filteredRooms.filter((sp) => sp.supplier === mappedSupplierFilter)
        : filteredRooms;

      // Should not show any rooms that are being verified in the current mappings (they are in the new mappings)
      const filteredHiddenCurrentMappings = supplierFilteredRooms.filter(
        (sp) => !appliedButNotSavedRooms.some((room) => room.externalId === sp.externalId),
      );

      return filteredHiddenCurrentMappings;
    }, [room.mappedRooms, room.externalId, removePayload, appliedButNotSavedRooms, mappedSupplierFilter]);

    const isVerificationPending = (mappedRoom: MappedRoom) =>
      newMappings.some(
        (m) => m.externalId === mappedRoom.externalId && m.supplier === mappedRoom.supplier && m.isVerified,
      );

    const groupedCurrentMappings = useMemo(() => {
      const grouped = groupMappedRoomsBySupplierName(currentMappings);

      // Sort: unverified/partially verified items first, then fully verified
      return grouped.sort((a, b) => {
        const isGroupA = isMappedRoomGroup(a);
        const isGroupB = isMappedRoomGroup(b);

        const roomsA = isGroupA ? a.rooms : [a];
        const roomsB = isGroupB ? b.rooms : [b];

        const verifiedCountA = roomsA.filter((r) => r.isVerified || isVerificationPending(r)).length;
        const verifiedCountB = roomsB.filter((r) => r.isVerified || isVerificationPending(r)).length;

        const allVerifiedA = verifiedCountA === roomsA.length;
        const allVerifiedB = verifiedCountB === roomsB.length;

        // If one is fully verified and the other isn't, unverified comes first
        if (allVerifiedA !== allVerifiedB) {
          return Number(allVerifiedA) - Number(allVerifiedB);
        }

        // Both have unverified rooms: sort by number of unverified (more unverified first)
        const unverifiedCountA = roomsA.length - verifiedCountA;
        const unverifiedCountB = roomsB.length - verifiedCountB;
        return unverifiedCountB - unverifiedCountA;
      });
    }, [currentMappings, newMappings]);

    const matchedMappingIdsSet = useMemo(() => new Set(matchedMappingIds ?? []), [matchedMappingIds]);
    const isSearchActive = Boolean(searchTerm && searchTerm.trim().length > 0);
    const filteredNewMappings = mappedSupplierFilter
      ? newMappings.filter((mapping) => mapping.supplier === mappedSupplierFilter)
      : newMappings;
    const hasVisibleMappings = currentMappings.length > 0;

    const sourceMappedRoom = room.mappedRooms.find((r) => r.externalId === room.externalId)?.sourceContent;

    const views = generateViewsText(sourceMappedRoom);

    const bedGroups = sourceMappedRoom?.bedGroups.map((x) => x.description).join(', ');

    const roomSizeSQM = sourceMappedRoom?.roomSize;
    let roomSizeText = roomSizeSQM ? `${roomSizeSQM} m²` : '';

    if (!roomSizeText) {
      roomSizeText = sourceMappedRoom?.roomSize?.toString() || 'N/A';
    }

    const { occupancyAge, occupancyAllowed } = generateOccupancyText(sourceMappedRoom);

    const handleVerifyRooms = (rooms: Array<MappedRoom>) => {
      // Filter rooms that need verification
      const roomsToVerify = rooms.filter((room) => !room.isVerified && !isVerificationPending(room));

      // Verify all rooms at once to avoid state race conditions
      roomsToVerify.forEach((room) => verifyMappedRoomModel(room));
    };

    const requestRemoveMappedRooms = (rooms: Array<MappedRoom>) => {
      const hasVerified = rooms.some((r) => r.isVerified);

      if (hasVerified) {
        setConfirmDelOpen(rooms);
      } else {
        onRemoveMappedRoom(rooms);
      }
    };

    const confirmMappedRoomDelete = () => {
      if (!confirmDelOpen) {
        return;
      }

      onRemoveMappedRoom(confirmDelOpen);
      setConfirmDelOpen(null);
    };

    // Check if this room has unverified mappings (pending verification)
    const hasUnverifiedMapping = newMappings.some((m) => !m.isVerified);
    const hasPendingMapping = appliedButNotSavedRooms.some((sr) =>
      room.mappedRooms.some((mr) => mr.externalId === sr.externalId),
    );
    const isPendingVerification = hasUnverifiedMapping || hasPendingMapping;

    return (
      <Paper
        ref={ref}
        id={`property-room-${room.id}`}
        variant="outlined"
        sx={{
          p: 2,
          backgroundColor: isPendingVerification ? '#f3e5f5' : !isSelected ? 'grey.100' : undefined,
          borderColor: isPendingVerification ? '#9c27b0' : isSelected ? 'info.light' : undefined,
          borderLeft: isPendingVerification ? '4px solid' : undefined,
          borderLeftColor: isPendingVerification ? '#9c27b0' : undefined,
        }}
      >
        <Stack direction="column" gap={1}>
          <FormControlLabel
            checked={isSelected}
            onChange={() => onSelect(room, room.supplier)}
            control={<Radio />}
            label={
              <Stack direction="row" justifyContent="space-between" width="100%" spacing={2} alignItems="center">
                <Typography variant="h6">{highlightText(room.name, searchTerm ?? '')}</Typography>
                <Stack direction="row" gap={1} alignItems="center">
                  {room.status && (
                    <Chip
                      label={room.status}
                      color={chipColorByStatus[room.status] ?? 'default'}
                      size="small"
                      sx={{ justifySelf: 'flex-end' }}
                    />
                  )}
                  {room.useOverrideNameForMapping && <Chip label="Use name override" color="success" size="small" />}
                </Stack>
              </Stack>
            }
            componentsProps={{ typography: { variant: 'h6' } }}
          />
          <Stack direction="column" gap={1}>
            <Stack direction="column">
              {room.nameOverride && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>
                    <strong>Room Override Name:</strong> {highlightText(room.nameOverride, searchTerm ?? '')}
                  </Typography>
                  {room.nameOverrideSource === 'accommodation' && (
                    <Chip label="Accommodation mapping" color="info" size="small" variant="outlined" />
                  )}
                </Stack>
              )}
              {room.externalId && (
                <Typography>
                  <strong>Supplier | External ID:</strong> {highlightText(room.externalId, searchTerm ?? '')}
                </Typography>
              )}
              {(() => {
                const out =
                  property.masterSupplier && room.externalId
                    ? extraInclusionsByKey?.get(`${property.masterSupplier}:${room.externalId}`) ?? []
                    : [];
                if (out.length === 0) return null;
                return (
                  <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                    <Typography component="span">
                      <strong>Inclusions:</strong>
                    </Typography>
                    <Tooltip title="Extracted from the room name, not the supplier's structured inclusion data. Surfaced for visibility only — not yet used for rate grouping.">
                      <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                    {out.map((inc) => (
                      <Chip key={inc.code} label={inc.label} color="info" size="small" />
                    ))}
                  </Stack>
                );
              })()}
              {views && (
                <Typography>
                  <strong>Views:</strong> {views}
                </Typography>
              )}
              {bedGroups && (
                <Typography>
                  <strong>Bed Groups:</strong> {bedGroups}
                </Typography>
              )}
              {roomSizeText && (
                <Typography>
                  <strong>Room Size:</strong> {roomSizeText}
                </Typography>
              )}
              {occupancyAge && (
                <Typography>
                  <strong>Occupancy Ages:</strong> {occupancyAge}
                </Typography>
              )}
              {occupancyAllowed && (
                <Typography>
                  <strong>Occupancy Allowed:</strong> {occupancyAllowed}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="column" gap={2}>
            <Stack direction="column" gap={2}>
              {(() => {
                // Separate verified and unverified mappings
                const verifiedMappings: typeof groupedCurrentMappings = [];
                const unverifiedMappings: typeof groupedCurrentMappings = [];

                groupedCurrentMappings.forEach((mappedItem) => {
                  const isGroup = isMappedRoomGroup(mappedItem);
                  const rooms = isGroup ? mappedItem.rooms : [mappedItem];
                  const verificationState = computeVerificationState(rooms, isVerificationPending);

                  if (verificationState.allVerified) {
                    verifiedMappings.push(mappedItem);
                  } else {
                    unverifiedMappings.push(mappedItem);
                  }
                });

                const lookupExtraInclusions = (rs: Array<MappedRoom>) => {
                  const seen = new Set<string>();
                  const out: Array<{ code: string; label: string }> = [];
                  for (const r of rs) {
                    const arr = extraInclusionsByKey?.get(`${r.supplier}:${r.externalId}`) ?? [];
                    for (const inc of arr) {
                      if (!seen.has(inc.code)) {
                        seen.add(inc.code);
                        out.push(inc);
                      }
                    }
                  }
                  return out;
                };

                const renderMapping = (mappedItem: (typeof groupedCurrentMappings)[number]) => {
                  const isGroup = isMappedRoomGroup(mappedItem);
                  const rooms = isGroup ? mappedItem.rooms : [mappedItem];
                  const referenceRoom = rooms[0];
                  const referenceSource = referenceRoom.sourceContent;
                  const mappedExtraInclusions = lookupExtraInclusions(rooms);
                  const verificationState = computeVerificationState(rooms, isVerificationPending);
                  const automappingState = computeAutomappingState(rooms, automappingFlagPayload);
                  const removeTooltip = isGroup
                    ? 'Remove Existing Mappings For Group'
                    : 'Remove Existing Mapping For Source Room';
                  const verifyTooltip = isGroup
                    ? 'Verify Existing Mappings For Group'
                    : 'Verify Existing Mapping For Source Room';

                  // Check if this specific mapping is from quick-map (pending save)
                  const isQuickMapped = appliedButNotSavedRooms.some((sr) =>
                    rooms.some((r) => r.externalId === sr.externalId && r.supplier === sr.supplier),
                  );

                  const chipProps = verificationState.allVerified
                    ? { label: 'Verified', color: 'success' as const }
                    : verificationState.someVerified
                    ? { label: 'Partially Verified', color: 'info' as const }
                    : isQuickMapped
                    ? {
                        label: 'Pending Save',
                        sx: {
                          backgroundColor: '#9c27b0', // Purple color
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#7b1fa2', // Darker purple on hover
                          },
                        },
                      }
                    : { label: 'Unverified', color: 'warning' as const };

                  const hasSearchMatch = isSearchActive && rooms.some((room) => matchedMappingIdsSet.has(room.id));
                  const accordionKeyBase = isGroup
                    ? `${referenceRoom.supplier}-${mappedItem.supplierRoomName}`
                    : referenceRoom.id;
                  const accordionKey = `${accordionKeyBase}-${isSearchActive ? 'search' : 'default'}`;
                  const accordionProps = {
                    defaultExpanded: isSearchActive ? hasSearchMatch : !verificationState.allVerified,
                  };

                  return (
                    <Accordion
                      key={accordionKey}
                      {...accordionProps}
                      sx={{
                        borderColor: verificationState.allVerified
                          ? 'success.light'
                          : verificationState.someVerified
                          ? 'info.light'
                          : 'warning.light',
                        borderWidth: '2px',
                        borderStyle: 'solid',
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                          {isQuickMapped ? (
                            <Chip
                              label="Pending Save"
                              size="small"
                              sx={{
                                backgroundColor: '#9c27b0',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#7b1fa2',
                                },
                              }}
                            />
                          ) : (
                            <Chip {...chipProps} size="small" />
                          )}
                          {isGroup && (
                            <Chip
                              label={`${rooms.length} ${rooms.length === 1 ? 'room' : 'rooms'}`}
                              color="info"
                              size="small"
                            />
                          )}
                          {(!verificationState.allVerified || enableMLMatching) &&
                            referenceRoom.supplier !== property.masterSupplier && (
                              <MappingConfidence
                                sourceRoomName={referenceSource?.name}
                                propertyRoomName={room.nameOverride ?? room.name}
                                mlMatchingCandidates={mlMatchingCandidates}
                              />
                            )}
                          <span>
                            {isGroup ? (
                              <>
                                ({referenceRoom.supplier}){' '}
                                {highlightText(mappedItem.supplierRoomName, searchTerm ?? '')} ({rooms.length}{' '}
                                {rooms.length === 1 ? 'room' : 'rooms'}) - {rooms.length}{' '}
                                {rooms.length === 1 ? 'room' : 'rooms'}
                              </>
                            ) : (
                              <>
                                ({referenceRoom.supplier}){' '}
                                {highlightText(
                                  referenceSource?.description && !['NA', 'N/A'].includes(referenceSource.description)
                                    ? referenceSource.description
                                    : referenceSource?.name ?? 'None Provided',
                                  searchTerm ?? '',
                                )}{' '}
                                - {highlightText(referenceRoom.externalId, searchTerm ?? '')}
                              </>
                            )}
                          </span>
                          {mappedExtraInclusions.length > 0 && (
                            <Stack
                              direction="row"
                              gap={1}
                              flexWrap="wrap"
                              alignItems="center"
                              sx={{ width: '100%', mt: 0.5 }}
                            >
                              <Typography variant="body2" component="span">
                                <strong>Inclusions:</strong>
                              </Typography>
                              <Tooltip title="Extracted from the room name, not the supplier's structured inclusion data. Surfaced for visibility only — not yet used for rate grouping.">
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                              {mappedExtraInclusions.map((inc) => (
                                <Chip key={inc.code} label={inc.label} color="info" size="small" />
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack direction="column">
                          <Box>
                            <Typography>
                              <strong>Supplier | External ID:</strong>{' '}
                              {isGroup ? (
                                `(${mappedItem.supplier}) ${rooms
                                  .map((r) => highlightText(r.externalId, searchTerm ?? ''))
                                  .reduce<Array<React.ReactNode>>(
                                    (acc, curr, idx) => (idx === 0 ? [curr] : [...acc, ', ', curr]),
                                    [],
                                  )}`
                              ) : (
                                <>
                                  ({referenceRoom.supplier}) {highlightText(referenceRoom.externalId, searchTerm ?? '')}
                                </>
                              )}
                            </Typography>
                            <Typography>
                              <strong>Views:</strong> {generateViewsText(referenceSource)}
                            </Typography>
                            <Typography>
                              <strong>Room Size:</strong> {generateRoomSizeText(referenceSource)}
                            </Typography>
                            <Typography>
                              <strong>Occupancy Age:</strong>{' '}
                              {generateOccupancyText(referenceSource).occupancyAge || 'N/A'}
                            </Typography>
                            <Typography>
                              <strong>Occupancy Allowed:</strong>{' '}
                              {generateOccupancyText(referenceSource).occupancyAllowed || 'N/A'}
                            </Typography>

                            <Typography>
                              <strong>Description</strong> {referenceSource?.description || 'N/A'}
                            </Typography>

                            {referenceRoom.mappingMethod && (
                              <Typography>
                                <strong>Mapping Method:</strong>{' '}
                                {referenceRoom.mappingMethod.length ? (
                                  referenceRoom.mappingMethod.join(', ')
                                ) : (
                                  <em>None Provided</em>
                                )}
                              </Typography>
                            )}

                            <Typography>
                              <strong>Availability Score:</strong> {referenceSource?.availabilityScore ?? 'N/A'}
                            </Typography>

                            <Typography>
                              <strong>Updated At:</strong>{' '}
                              {referenceRoom.updatedAt
                                ? new Date(referenceRoom.updatedAt).toLocaleString()
                                : 'No date found.'}
                            </Typography>

                            <FormControlLabel
                              control={<Switch checked={automappingState.checked} />}
                              label="Is Automapping Enabled"
                              onChange={(_event: SyntheticEvent, checked: boolean) =>
                                handleSetAutomappingFlags(buildAutomappingTargets(rooms, checked))
                              }
                            />
                            {automappingState.hasOverride && (
                              <Typography variant="caption" color="text.secondary">
                                Pending automapping overrides will apply to one or more rooms in this group.
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </AccordionDetails>
                      <AccordionActions>
                        <RoomSourceCommentModal room={referenceSource} />
                        <RoomSourceContentModal sourceContent={referenceSource} />
                        {
                          <Tooltip title={removeTooltip}>
                            <Button
                              startIcon={<DeleteForever />}
                              onClick={() => requestRemoveMappedRooms(rooms)}
                              variant="contained"
                              color="error"
                            >
                              Remove{isGroup ? ' Group' : ''}
                            </Button>
                          </Tooltip>
                        }
                        {!verificationState.allVerified && (
                          <Tooltip title={verifyTooltip}>
                            <Button
                              startIcon={<CheckCircleOutline />}
                              onClick={() => handleVerifyRooms(rooms)}
                              variant="contained"
                              color="success"
                            >
                              Verify{isGroup ? ' Group' : ''}
                            </Button>
                          </Tooltip>
                        )}
                      </AccordionActions>
                    </Accordion>
                  );
                };

                return (
                  <>
                    {/* Render unverified mappings in a collapsed accordion (expands on search) */}
                    {unverifiedMappings.length > 0 && (
                      <Accordion
                        key={`unverified-${room.id}-${isSearchActive ? 'search' : 'default'}`}
                        defaultExpanded={
                          isSearchActive &&
                          unverifiedMappings.some((mappedItem) => {
                            const isGroup = isMappedRoomGroup(mappedItem);
                            const rooms = isGroup ? mappedItem.rooms : [mappedItem];

                            // Check if any room in this mapping matches the search
                            return rooms.some((mappedRoom) => {
                              const source = mappedRoom.sourceContent;
                              const normalizedSearch = (searchTerm ?? '').toLowerCase();

                              // Check room name, external ID, supplier room name, and description
                              return (
                                matchedMappingIdsSet.has(mappedRoom.id) ||
                                mappedRoom.externalId.toLowerCase().includes(normalizedSearch) ||
                                (source?.name && source.name.toLowerCase().includes(normalizedSearch)) ||
                                (source?.description && source.description.toLowerCase().includes(normalizedSearch)) ||
                                (isGroup && mappedItem.supplierRoomName.toLowerCase().includes(normalizedSearch))
                              );
                            });
                          })
                        }
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            backgroundColor: 'warning.light',
                            '&:hover': {
                              backgroundColor: 'warning.main',
                            },
                          }}
                        >
                          <Stack direction="row" gap={1} alignItems="center">
                            <Chip label="Unverified" color="warning" size="small" />
                            <Typography variant="subtitle1" fontWeight="medium">
                              {unverifiedMappings.length} Unverified Source{' '}
                              {pluralize('Room', unverifiedMappings.length)}
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack direction="column" gap={2}>
                            {unverifiedMappings.map((mappedItem) => {
                              const isGroup = isMappedRoomGroup(mappedItem);
                              const rooms = isGroup ? mappedItem.rooms : [mappedItem];
                              const referenceRoom = rooms[0];
                              const referenceSource = referenceRoom.sourceContent;
                              const mappedExtraInclusions = lookupExtraInclusions(rooms);
                              const verificationState = computeVerificationState(rooms, isVerificationPending);
                              const automappingState = computeAutomappingState(rooms, automappingFlagPayload);
                              const removeTooltip = isGroup
                                ? 'Remove Existing Mappings For Group'
                                : 'Remove Existing Mapping For Source Room';
                              const verifyTooltip = isGroup
                                ? 'Verify Existing Mappings For Group'
                                : 'Verify Existing Mapping For Source Room';

                              // Check if this specific mapping is from quick-map (pending save)
                              const isQuickMapped = appliedButNotSavedRooms.some((sr) =>
                                rooms.some((r) => r.externalId === sr.externalId && r.supplier === sr.supplier),
                              );

                              const chipProps = isQuickMapped
                                ? {
                                    label: 'Pending Save',
                                    sx: {
                                      backgroundColor: '#9c27b0',
                                      color: 'white',
                                      '&:hover': {
                                        backgroundColor: '#7b1fa2',
                                      },
                                    },
                                  }
                                : { label: 'Unverified', color: 'warning' as const };

                              const keyBase = isGroup
                                ? `${referenceRoom.supplier}-${mappedItem.supplierRoomName}`
                                : referenceRoom.id;

                              return (
                                <Paper
                                  key={keyBase}
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    borderColor: 'warning.light',
                                    borderWidth: '2px',
                                  }}
                                >
                                  <Stack direction="column" gap={1}>
                                    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                                      {isQuickMapped ? (
                                        <Chip
                                          label="Pending Save"
                                          size="small"
                                          sx={{
                                            backgroundColor: '#9c27b0',
                                            color: 'white',
                                            '&:hover': {
                                              backgroundColor: '#7b1fa2',
                                            },
                                          }}
                                        />
                                      ) : (
                                        <Chip {...chipProps} size="small" />
                                      )}
                                      {isGroup && (
                                        <Chip
                                          label={`${rooms.length} ${rooms.length === 1 ? 'room' : 'rooms'}`}
                                          color="info"
                                          size="small"
                                        />
                                      )}
                                      {(!verificationState.allVerified || enableMLMatching) &&
                                        referenceRoom.supplier !== Suppliers.expedia && (
                                          <MappingConfidence
                                            sourceRoomName={referenceSource?.name}
                                            propertyRoomName={room.nameOverride ?? room.name}
                                            mlMatchingCandidates={mlMatchingCandidates}
                                          />
                                        )}
                                      <Typography variant="body1" fontWeight="medium">
                                        {isGroup ? (
                                          <>
                                            ({referenceRoom.supplier}){' '}
                                            {highlightText(mappedItem.supplierRoomName, searchTerm ?? '')} (
                                            {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'})
                                          </>
                                        ) : (
                                          <>
                                            ({referenceRoom.supplier}){' '}
                                            {highlightText(
                                              referenceSource?.description &&
                                                !['NA', 'N/A'].includes(referenceSource.description)
                                                ? referenceSource.description
                                                : referenceSource?.name ?? 'None Provided',
                                              searchTerm ?? '',
                                            )}{' '}
                                            - {highlightText(referenceRoom.externalId, searchTerm ?? '')}
                                          </>
                                        )}
                                      </Typography>
                                    </Stack>

                                    {mappedExtraInclusions.length > 0 && (
                                      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                                        <Typography variant="body2" component="span">
                                          <strong>Inclusions:</strong>
                                        </Typography>
                                        <Tooltip title="Extracted from the room name, not the supplier's structured inclusion data. Surfaced for visibility only — not yet used for rate grouping.">
                                          <InfoOutlined
                                            sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }}
                                          />
                                        </Tooltip>
                                        {mappedExtraInclusions.map((inc) => (
                                          <Chip key={inc.code} label={inc.label} color="info" size="small" />
                                        ))}
                                      </Stack>
                                    )}

                                    <Typography variant="body2">
                                      <strong>Supplier | External ID:</strong>{' '}
                                      {isGroup ? (
                                        <>
                                          ({mappedItem.supplier}){' '}
                                          {rooms.map((r, idx) => (
                                            <React.Fragment key={r.externalId}>
                                              {idx > 0 && ', '}
                                              {highlightText(r.externalId, searchTerm ?? '')}
                                            </React.Fragment>
                                          ))}
                                        </>
                                      ) : (
                                        <>
                                          ({referenceRoom.supplier}){' '}
                                          {highlightText(referenceRoom.externalId, searchTerm ?? '')}
                                        </>
                                      )}
                                    </Typography>

                                    <Typography variant="body2">
                                      <strong>Views:</strong> {generateViewsText(referenceSource)}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Room Size:</strong> {generateRoomSizeText(referenceSource) || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Occupancy Age:</strong>{' '}
                                      {generateOccupancyText(referenceSource).occupancyAge || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Occupancy Allowed:</strong>{' '}
                                      {generateOccupancyText(referenceSource).occupancyAllowed || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Description:</strong> {referenceSource?.description || 'N/A'}
                                    </Typography>

                                    {referenceRoom.mappingMethod && referenceRoom.mappingMethod.length > 0 && (
                                      <Typography variant="body2">
                                        <strong>Mapping Method:</strong> {referenceRoom.mappingMethod.join(', ')}
                                      </Typography>
                                    )}

                                    <Typography variant="body2">
                                      <strong>Availability Score:</strong> {referenceSource?.availabilityScore ?? 'N/A'}
                                    </Typography>

                                    <Typography variant="body2">
                                      <strong>Updated At:</strong>{' '}
                                      {referenceRoom.updatedAt
                                        ? new Date(referenceRoom.updatedAt).toLocaleString()
                                        : 'No date found.'}
                                    </Typography>

                                    <FormControlLabel
                                      control={<Switch checked={automappingState.checked} />}
                                      label="Is Automapping Enabled"
                                      onChange={(_event: SyntheticEvent, checked: boolean) => {
                                        const targets = buildAutomappingTargets(rooms, checked);
                                        handleSetAutomappingFlags(targets);
                                      }}
                                    />
                                    {automappingState.hasOverride && (
                                      <Typography variant="caption" color="text.secondary">
                                        Pending automapping overrides will apply to one or more rooms in this group.
                                      </Typography>
                                    )}

                                    <Stack direction="row" gap={1} justifyContent="flex-end" alignItems="center">
                                      {!isGroup && <RoomSourceCommentModal room={referenceSource} />}
                                      {!isGroup && referenceSource && (
                                        <RoomSourceContentModal sourceContent={referenceSource} />
                                      )}
                                      <Tooltip title={removeTooltip} placement="top" arrow>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          color="error"
                                          startIcon={<DeleteForever />}
                                          onClick={() => {
                                            onRemoveMappedRoom(rooms);
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      </Tooltip>
                                      <Tooltip title={verifyTooltip} placement="top" arrow>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          color="success"
                                          disabled={verificationState.allVerified}
                                          startIcon={<CheckCircleOutline />}
                                          onClick={() => {
                                            rooms.forEach((r) => verifyMappedRoomModel(r));
                                          }}
                                        >
                                          Verify
                                        </Button>
                                      </Tooltip>
                                    </Stack>
                                  </Stack>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Render verified mappings in a collapsed accordion */}
                    {verifiedMappings.length > 0 && (
                      <Accordion
                        key={`verified-${room.id}-${isSearchActive ? 'search' : 'default'}`}
                        defaultExpanded={
                          isSearchActive
                            ? verifiedMappings.some((mappedItem) => {
                                const rooms = isMappedRoomGroup(mappedItem) ? mappedItem.rooms : [mappedItem];
                                return rooms.some((mappedRoom) => matchedMappingIdsSet.has(mappedRoom.id));
                              })
                            : false
                        }
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            backgroundColor: 'success.light',
                            '&:hover': {
                              backgroundColor: 'success.main',
                            },
                          }}
                        >
                          <Stack direction="row" gap={1} alignItems="center">
                            <Chip label="Verified" color="success" size="small" />
                            <Typography variant="subtitle1" fontWeight="medium">
                              {verifiedMappings.length} Verified Source {pluralize('Room', verifiedMappings.length)}
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack direction="column" gap={2}>
                            {verifiedMappings.map(renderMapping)}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </>
                );
              })()}
            </Stack>
            {!hasVisibleMappings && mappedSupplierFilter && filteredNewMappings.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No mapped rooms for the selected supplier.
              </Typography>
            )}
            {filteredNewMappings.length > 0 && (
              <>
                <Divider />
                <Stack direction="column" gap={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="info.main">
                      New Mappings
                    </Typography>
                    <Button startIcon={<DeleteForever />} onClick={clearNewMappings} variant="contained" color="error">
                      Clear new Mappings
                    </Button>
                  </Stack>
                  <Stack direction="column">
                    {filteredNewMappings.map((mapping) => (
                      <ToBeMappedRoomElement
                        key={`${mapping.supplier}-${mapping.externalId}`}
                        mapping={mapping}
                        appliedButNotSavedRooms={appliedButNotSavedRooms}
                        onRemoveMapping={(mapping) => removeSingleMapping(mapping.externalId, mapping.supplier)}
                      />
                    ))}
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
        <Dialog open={Boolean(confirmDelOpen)} onClose={() => setConfirmDelOpen(null)}>
          {confirmDelOpen && (
            <>
              <DialogTitle>
                Are you sure you want to delete the selected verified mapping
                {confirmDelOpen.length > 1 ? 's' : ''}?
              </DialogTitle>
              <Stack direction="row" justifyContent="space-between" p={3}>
                <Button onClick={() => setConfirmDelOpen(null)} variant="outlined" color="primary">
                  Cancel
                </Button>
                <Button onClick={confirmMappedRoomDelete} variant="contained" color="error">
                  Confirm Delete
                </Button>
              </Stack>
            </>
          )}
        </Dialog>
      </Paper>
    );
  }),
);

export default ControlRoomElement;
